/**
 * Provider stack and the top-level error boundary.
 *
 * Kept separate from `App` so the boundary can render a useful screen even when
 * `App` itself is what failed, and so tests can mount either layer.
 */
import { Component, useEffect, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { App } from './App';
import { I18nProvider, resolveLocale } from './i18n';
import type { Locale } from './i18n';
import { ToastProvider } from './components/ui';
import { useAppStore } from './store/appStore';
import { BRANDING } from './config/branding';

export function AppProviders() {
  const localePreference = useAppStore((state) => state.preferences.locale);
  const [locale, setLocale] = useState<Locale>(() =>
    resolveLocale(localePreference, navigatorLanguages()),
  );

  useEffect(() => {
    setLocale(resolveLocale(localePreference, navigatorLanguages()));
  }, [localePreference]);

  return (
    <ErrorBoundary>
      <I18nProvider locale={locale}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

function navigatorLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages ?? (navigator.language ? [navigator.language] : []);
}

/* ------------------------------------------------------------------ *
 * Error boundary
 * ------------------------------------------------------------------ */

interface BoundaryState {
  readonly error: Error | null;
}

/**
 * Deliberately not translated: if the app crashed, the i18n provider is one of
 * the things that might have crashed with it.
 */
class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[FlowScript] Unhandled error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md rounded-panel border border-border bg-surface p-6 shadow-panel">
          <h1 className="text-lg font-semibold text-ink">{BRANDING.name} hit a problem</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Your work is saved locally. Reloading usually clears this.
          </p>
          <pre className="fs-scrollbar mt-3 max-h-40 overflow-auto rounded bg-surface-sunken p-2 font-mono text-xs text-ink-muted">
            {error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-accent px-3 py-2 text-sm font-medium text-accent-ink"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
